import { useState, useEffect, useCallback } from 'react'
import { FiTrash2, FiRefreshCw, FiEye, FiEdit2 } from 'react-icons/fi'
import {
  printables as printablesApi,
  notebooks as notebooksApi,
  themes as themesApi,
  type AdminPrintableSummary,
  type NotebookSummary,
  type NotebookItem,
  type ThemeSummary,
} from '../api/client'
import PageHeader from './PageHeader'
import { useTranslation } from '../i18n/LanguageContext'
import {
  useNotebookItemsBuilder,
  NotebookItemsList,
  NotebookActivityForm,
  type NotebookBuilderItem,
} from './NotebookItemsBuilder'

async function openPreview(url: string) {
  window.open(url, '_blank', 'noopener')
}

// ── Shared helpers ────────────────────────────────────────────────────────────────────────────

type Tier = 'BASIC' | 'PLUS' | 'PREMIUM'

const TIER_COLORS: Record<Tier, string> = {
  BASIC:   '#4CAF50',
  PLUS:    '#2196F3',
  PREMIUM: '#9C27B0',
}

const STATUS_COLORS: Record<string, string> = {
  PENDING:    '#888',
  GENERATING: '#FF9800',
  DONE:       '#4CAF50',
  FAILED:     '#F44336',
}

type ActivitySlug = 'memory' | 'wordmatch'

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display:       'inline-block',
      fontSize: '0.875rem',
      fontWeight:    700,
      color:         '#fff',
      background:    color,
      borderRadius:  6,
      padding:       '2px 7px',
      letterSpacing: '0.03em',
    }}>
      {label}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  return <Badge label={status} color={STATUS_COLORS[status] ?? '#888'} />
}

function TierBadge({ tier }: { tier: Tier }) {
  return <Badge label={tier} color={TIER_COLORS[tier]} />
}

// ── Create Printable Modal ────────────────────────────────────────────────────────────────────────────

interface CreatePrintableModalProps {
  onClose: () => void
  onCreated: (pair: { es: AdminPrintableSummary; en: AdminPrintableSummary }) => void
}

function CreatePrintableModal({ onClose, onCreated }: CreatePrintableModalProps) {
  const [titleEs, setTitleEs]   = useState('')
  const [titleEn, setTitleEn]   = useState('')
  const [activity, setActivity] = useState<ActivitySlug>('memory')
  const [mode, setMode]         = useState<'definitions' | 'anagram'>('definitions')
  const [themeId, setThemeId]   = useState<number | ''>('')
  const [tier, setTier]         = useState<Tier>('BASIC')
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [allowedThemes, setAllowedThemes] = useState<ThemeSummary[]>([])

  useEffect(() => {
    themesApi.list(activity).then(setAllowedThemes).catch(() => setAllowedThemes([]))
  }, [activity])

  const publishedThemes = allowedThemes.filter(t => t.published)

  async function handleSubmit() {
    if (!titleEs.trim() && !titleEn.trim()) { setError('Al menos un título (ES o EN) es obligatorio'); return }
    if (!themeId) { setError('Selecciona un tema'); return }

    setSaving(true); setError(null)
    try {
      const created = await printablesApi.create({
        titleEs:      titleEs.trim() || undefined,
        titleEn:      titleEn.trim() || undefined,
        activitySlug: activity,
        themeId:      themeId as number,
        requiredTier: tier,
        mode:         activity === 'wordmatch' ? mode : undefined,
      })
      onCreated(created)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al crear')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.box} onClick={e => e.stopPropagation()}>
        <h2 style={modalStyles.title}>Nuevo imprimible</h2>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={modalStyles.label}>Título (ES)</label>
            <input
              style={modalStyles.input}
              value={titleEs}
              onChange={e => setTitleEs(e.target.value)}
              placeholder="Ej. Animales — Memoria"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={modalStyles.label}>Título (EN)</label>
            <input
              style={modalStyles.input}
              value={titleEn}
              onChange={e => setTitleEn(e.target.value)}
              placeholder="Eg. Animals — Memory"
            />
          </div>
        </div>

        <label style={modalStyles.label}>Actividad</label>
        <select style={modalStyles.input} value={activity} onChange={e => setActivity(e.target.value as ActivitySlug)}>
          <option value="memory">Memoria (imágenes + palabras)</option>
          <option value="wordmatch">Palabras (definiciones)</option>
        </select>

        {activity === 'wordmatch' && (
          <>
            <label style={modalStyles.label}>Modo</label>
            <select style={modalStyles.input} value={mode} onChange={e => setMode(e.target.value as 'definitions' | 'anagram')}>
              <option value="definitions">Definiciones</option>
              <option value="anagram">Anagrama</option>
            </select>
          </>
        )}

        <label style={modalStyles.label}>Tema</label>
        <select style={modalStyles.input} value={themeId} onChange={e => setThemeId(Number(e.target.value) || '')}>
          <option value="">— Selecciona un tema —</option>
          {publishedThemes.map(t => (
            <option key={t.id} value={t.id}>
              {t.nameEs} ({t.imageCount} imágenes)
            </option>
          ))}
        </select>

        <label style={modalStyles.label}>Nivel de suscripción</label>
        <select style={modalStyles.input} value={tier} onChange={e => setTier(e.target.value as Tier)}>
          <option value="BASIC">Básico</option>
          <option value="PLUS">Plus</option>
          <option value="PREMIUM">Premium</option>
        </select>

        {error && <p style={modalStyles.error}>{error}</p>}

        <div style={modalStyles.actions}>
          <button style={modalStyles.btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
          <button style={modalStyles.btnPrimary} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Guardando…' : 'Crear borrador'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Create Notebook Modal ─────────────────────────────────────────────────────────────────────────────

interface CreateNotebookModalProps {
  onClose: () => void
  onCreated: (pair: { es: NotebookSummary; en: NotebookSummary }) => void
}

function CreateNotebookModal({ onClose, onCreated }: CreateNotebookModalProps) {
  const { t } = useTranslation()
  const games = t.dashboard.games as Record<string, { label: string }>
  const diffLabels = { easy: 'Fácil', medium: 'Medio', hard: 'Difícil' }

  const [titleEs, setTitleEs]   = useState('')
  const [titleEn, setTitleEn]   = useState('')
  const [tier, setTier]         = useState<Tier>('BASIC')
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const [wsThemes, setWsThemes] = useState<ThemeSummary[]>([])
  const [atThemes, setAtThemes] = useState<ThemeSummary[]>([])

  useEffect(() => {
    themesApi.list('wordsearch').then(setWsThemes).catch(() => setWsThemes([]))
    themesApi.list('attention').then(setAtThemes).catch(() => setAtThemes([]))
  }, [])

  const builder = useNotebookItemsBuilder({
    language: 'es',
    wordsearchThemes: wsThemes,
    attentionThemes: atThemes,
    diffLabels,
    games,
  })

  async function handleSubmit() {
    if (!titleEs.trim() && !titleEn.trim()) { setError('Al menos un título (ES o EN) es obligatorio'); return }
    if (builder.items.length === 0) { setError('Añade al menos un elemento'); return }
    setSaving(true); setError(null)
    try {
      const created = await notebooksApi.create({
        titleEs: titleEs.trim() || undefined,
        titleEn: titleEn.trim() || undefined,
        requiredTier: tier,
        items: builder.items.map(i => ({ itemType: 'random' as const, spec: i.spec })),
      })
      onCreated(created)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al crear')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={{ ...modalStyles.box, maxWidth: 660, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={modalStyles.title}>Nuevo cuaderno</h2>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={modalStyles.label}>Título (ES)</label>
            <input style={modalStyles.input} value={titleEs} onChange={e => setTitleEs(e.target.value)} placeholder="Ej. Cuaderno de actividades — Primavera" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={modalStyles.label}>Título (EN)</label>
            <input style={modalStyles.input} value={titleEn} onChange={e => setTitleEn(e.target.value)} placeholder="Eg. Activity notebook — Spring" />
          </div>
        </div>

        <label style={modalStyles.label}>Nivel de suscripción</label>
        <select style={modalStyles.input} value={tier} onChange={e => setTier(e.target.value as Tier)}>
          <option value="BASIC">Básico</option>
          <option value="PLUS">Plus</option>
          <option value="PREMIUM">Premium</option>
        </select>

        {/* ── Current item list ──────────────────────────────────────────────────────────────────────────── */}
        <label style={modalStyles.label}>Contenido del cuaderno ({builder.items.length})</label>
        <NotebookItemsList
          items={builder.items}
          onRemove={builder.removeItem}
          onMoveUp={builder.moveUp}
          onMoveDown={builder.moveDown}
          showReorder
          emptyLabel="Añade actividades generadas aleatoriamente."
        />

        {/* ── Activity form ──────────────────────────────────────────────────────────────────────────────── */}
        <NotebookActivityForm
          hook={builder}
          language="es"
          wordsearchThemes={wsThemes}
          attentionThemes={atThemes}
          labelStyle={modalStyles.label}
          inputStyle={modalStyles.input}
          btnPrimaryStyle={modalStyles.btnPrimary}
          requireThemeSelection
        />

        {error && <p style={modalStyles.error}>{error}</p>}
        <div style={modalStyles.actions}>
          <button style={modalStyles.btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
          <button style={modalStyles.btnPrimary} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Guardando…' : 'Crear cuaderno'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Edit Notebook Modal ─────────────────────────────────────────────────────────────────────────────

interface EditNotebookModalProps {
  notebook: NotebookSummary
  onClose: () => void
  onUpdated: (updated: NotebookSummary) => void
}

function EditNotebookModal({ notebook, onClose, onUpdated }: EditNotebookModalProps) {
  const { t } = useTranslation()
  const games = t.dashboard.games as Record<string, { label: string }>
  const diffLabels = { easy: 'Fácil', medium: 'Medio', hard: 'Difícil' }

  const [title, setTitle]             = useState(notebook.title)
  const [tier, setTier]               = useState<Tier>(notebook.requiredTier)
  const [saving, setSaving]           = useState(false)
  const [error, setError]             = useState<string | null>(null)

  const [wsThemes, setWsThemes]       = useState<ThemeSummary[]>([])
  const [atThemes, setAtThemes]       = useState<ThemeSummary[]>([])

  useEffect(() => {
    themesApi.list('wordsearch').then(setWsThemes).catch(() => setWsThemes([]))
    themesApi.list('attention').then(setAtThemes).catch(() => setAtThemes([]))
  }, [])

  // Convert existing NotebookItem[] to NotebookBuilderItem[] for the builder
  const initialBuilderItems = (notebook.items ?? [])
    .filter((i: NotebookItem) => i.itemType === 'random')
    .map((i: NotebookItem) => {
      if (i.itemType !== 'random') return null
      const spec = i.spec
      const typeLabel = games[spec.type]?.label ?? spec.type
      let detail = ''
      if (spec.type === 'wordsearch') detail = ` · ${spec.difficulty}${spec.mode !== 'simple' ? ` · ${spec.mode}` : ''}`
      else if (spec.type === 'sudoku') detail = ` · ${spec.mode === 'mini' ? '4×4' : '9×9'} · ${spec.difficulty}`
      else if (spec.type === 'maths') detail = ` · ${spec.operation} · ${spec.difficulty}`
      else if (spec.type === 'attention') detail = ` · ${spec.difficulty}${spec.mode ? ` · ${spec.mode}` : ''}`
      else if (spec.type === 'symmetry') detail = ` · ${spec.mode} · ${spec.difficulty}`
      else if (spec.type === 'whats_missing') detail = ` · ${spec.mode} · ${spec.difficulty}`
      else if (spec.type === 'roman') detail = ` · ${spec.difficulty}`
      else if (spec.type === 'hours') detail = ` · ${spec.difficulty} · ${spec.clockType}`
      else if (spec.type === 'synonyms') detail = ` · ${spec.gameMode} · ${spec.printMode}`
      return { itemType: 'random' as const, spec, label: `×${spec.count} ${typeLabel}${detail}` }
    })
    .filter(Boolean) as NotebookBuilderItem[]

  const builder = useNotebookItemsBuilder({
    language: notebook.language,
    wordsearchThemes: wsThemes,
    attentionThemes: atThemes,
    diffLabels,
    games,
    initialItems: initialBuilderItems,
  })

  async function handleSubmit() {
    if (!title.trim()) { setError('El título es obligatorio'); return }
    if (builder.items.length === 0) { setError('Añade al menos un elemento'); return }
    setSaving(true); setError(null)
    try {
      const updated = await notebooksApi.update(notebook.id, {
        title: title.trim(),
        requiredTier: tier,
        items: builder.items.map(i => ({ itemType: 'random' as const, spec: i.spec })),
      })
      onUpdated(updated)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al actualizar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={{ ...modalStyles.box, maxWidth: 660, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={modalStyles.title}>Editar cuaderno</h2>

        <label style={modalStyles.label}>Título</label>
        <input style={modalStyles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Título del cuaderno" />

        <label style={modalStyles.label}>Nivel de suscripción</label>
        <select style={modalStyles.input} value={tier} onChange={e => setTier(e.target.value as Tier)}>
          <option value="BASIC">Básico</option>
          <option value="PLUS">Plus</option>
          <option value="PREMIUM">Premium</option>
        </select>

        {/* ── Current item list ──────────────────────────────────────────────────────────────────────────── */}
        <label style={modalStyles.label}>Contenido del cuaderno ({builder.items.length})</label>
        <NotebookItemsList
          items={builder.items}
          onRemove={builder.removeItem}
          onMoveUp={builder.moveUp}
          onMoveDown={builder.moveDown}
          showReorder
          emptyLabel="Añade actividades generadas aleatoriamente."
        />

        {/* ── Activity form ──────────────────────────────────────────────────────────────────────────────── */}
        <NotebookActivityForm
          hook={builder}
          language={notebook.language}
          wordsearchThemes={wsThemes}
          attentionThemes={atThemes}
          labelStyle={modalStyles.label}
          inputStyle={modalStyles.input}
          btnPrimaryStyle={modalStyles.btnPrimary}
          requireThemeSelection
        />

        {error && <p style={modalStyles.error}>{error}</p>}
        <div style={modalStyles.actions}>
          <button style={modalStyles.btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
          <button style={modalStyles.btnPrimary} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Printable Row ──────────────────────────────────────────────────────────────────────────────────────────

interface PrintableRowProps {
  p: AdminPrintableSummary
  onGenerate: (id: number) => void
  onTogglePublish: (id: number, published: boolean) => void
  onDelete: (id: number) => void
  generating: boolean
}

function PrintableRow({ p, onGenerate, onTogglePublish, onDelete, generating }: PrintableRowProps) {
  const [previewing, setPreviewing] = useState(false)

  async function handlePreview() {
    setPreviewing(true)
    try {
      const { url } = await printablesApi.preview(p.id)
      openPreview(url)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error al obtener previsualización')
    } finally {
      setPreviewing(false)
    }
  }

  return (
    <div style={rowStyles.row}>
      <div style={rowStyles.info}>
        <span style={rowStyles.title}>{p.title}</span>
        <span style={rowStyles.meta}>
          {p.type.replace(/_/g, ' ')} · {p.language.toUpperCase()}
          {p.themeName && ` · ${p.themeName.es}`}
        </span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          <TierBadge tier={p.requiredTier} />
          <StatusBadge status={p.generationStatus} />
          {p.published && <Badge label="Publicado" color="#1B4332" />}
          {p.generationError && (
            <span style={{ fontSize: '0.875rem', color: '#F44336' }} title={p.generationError}>⚠ Error</span>
          )}
        </div>
      </div>
      <div style={rowStyles.actions}>
        <button
          style={{ ...rowStyles.btn, background: '#1B4332', color: '#fff' }}
          onClick={() => onGenerate(p.id)}
          disabled={generating || p.generationStatus === 'GENERATING'}
          title="Generar PDF"
        >
          {p.generationStatus === 'GENERATING' ? '…' : <><FiRefreshCw size={13} style={{ marginRight: 4 }} />Generar</>}
        </button>
        {p.hasFile && (
          <>
            <button
              style={{ ...rowStyles.btn, background: '#F0F4F0', color: '#1B4332' }}
              onClick={handlePreview}
              disabled={previewing}
              title="Previsualizar PDF"
            >
              {previewing ? '…' : <><FiEye size={13} style={{ marginRight: 4 }} />Preview</>}
            </button>
            <button
              style={{ ...rowStyles.btn, background: p.published ? '#E8DDD0' : '#2196F3', color: p.published ? '#333' : '#fff' }}
              onClick={() => onTogglePublish(p.id, !p.published)}
              title={p.published ? 'Despublicar' : 'Publicar'}
            >
              {p.published ? 'Despublicar' : 'Publicar'}
            </button>
          </>
        )}
        <button
          style={{ ...rowStyles.btn, background: '#FDECEA', color: '#C62828' }}
          onClick={() => { if (confirm(`¿Eliminar "${p.title}"?`)) onDelete(p.id) }}
          title="Eliminar"
        >
          <FiTrash2 size={14} />
        </button>
      </div>
    </div>
  )
}

// ── Notebook Row ────────────────────────────────────────────────────────────────────────────────────

interface NotebookRowProps {
  n: NotebookSummary
  onGenerate: (id: number) => void
  onTogglePublish: (id: number, published: boolean) => void
  onDelete: (id: number) => void
  onEdit: () => void
  generating: boolean
}

function NotebookRow({ n, onGenerate, onTogglePublish, onDelete, onEdit, generating }: NotebookRowProps) {
  const { t } = useTranslation()
  const [previewing, setPreviewing] = useState(false)

  async function handlePreview() {
    setPreviewing(true)
    try {
      const { url } = await notebooksApi.preview(n.id)
      openPreview(url)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error al obtener previsualización')
    } finally {
      setPreviewing(false)
    }
  }

  return (
    <div style={rowStyles.row}>
      <div style={rowStyles.info}>
        <span style={rowStyles.title}>{n.title}</span>
        <span style={rowStyles.meta}>{n.items?.length ?? 0} imprimibles</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          <TierBadge tier={n.requiredTier} />
          <StatusBadge status={n.generationStatus} />
          {n.published && <Badge label="Publicado" color="#1B4332" />}
        </div>
        {n.items && n.items.length > 0 && (
          <div style={{ fontSize: '0.875rem', color: '#888', marginTop: 4 }}>
            {n.items.map((i: NotebookItem, idx) => {
              let label: string
              if (i.itemType === 'printable') {
                label = i.title
              } else if (i.itemType === 'random') {
                const games = t.dashboard.games as Record<string, { label: string }>
                const typeLabel = games[i.spec.type]?.label ?? i.spec.type
                label = `×${i.spec.count} ${typeLabel}`
              } else {
                label = `${i.spec.type.replace(/_/g, ' ')} (${i.spec.themeIds.length} tema${i.spec.themeIds.length > 1 ? 's' : ''})`
              }
              return `${idx + 1}. ${label}`
            }).join(' · ')}
          </div>
        )}
      </div>
      <div style={rowStyles.actions}>
        <button
          style={{ ...rowStyles.btn, background: '#1B4332', color: '#fff' }}
          onClick={() => onGenerate(n.id)}
          disabled={generating || n.generationStatus === 'GENERATING'}
        >
          {n.generationStatus === 'GENERATING' ? '⏳' : '⚙ Generar'}
        </button>
        {n.hasFile && (
          <>
            <button
              style={{ ...rowStyles.btn, background: '#F0F4F0', color: '#1B4332' }}
              onClick={handlePreview}
              disabled={previewing}
              title="Previsualizar PDF"
            >
              {previewing ? '…' : <><FiEye size={13} style={{ marginRight: 4 }} />Preview</>}
            </button>
            <button
              style={{ ...rowStyles.btn, background: n.published ? '#E8DDD0' : '#2196F3', color: n.published ? '#333' : '#fff' }}
              onClick={() => onTogglePublish(n.id, !n.published)}
            >
              {n.published ? 'Despublicar' : 'Publicar'}
            </button>
          </>
        )}
        <button
          style={{ ...rowStyles.btn, background: '#F0EAE0', color: '#1B4332' }}
          onClick={onEdit}
          title="Editar cuaderno"
        >
          <FiEdit2 size={14} style={{ marginRight: 4 }} />Editar
        </button>
        <button
          style={{ ...rowStyles.btn, background: '#FDECEA', color: '#C62828' }}
          onClick={() => { if (confirm(`¿Eliminar cuaderno "${n.title}"?`)) onDelete(n.id) }}
        >
          <FiTrash2 size={14} />
        </button>
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────────────────────────────

interface AdminPrintablesPageProps {
  onBack: () => void
}

export default function AdminPrintablesPage({ onBack }: AdminPrintablesPageProps) {
  const [tab, setTab]                     = useState<'printables' | 'notebooks'>('printables')
  const [printablesList, setPrintables]   = useState<AdminPrintableSummary[]>([])
  const [notebooksList, setNotebooks]     = useState<NotebookSummary[]>([])
  const [loading, setLoading]             = useState(true)
  const [generatingIds, setGeneratingIds] = useState<Set<number>>(new Set())
  const [showCreateP, setShowCreateP]     = useState(false)
  const [showCreateN, setShowCreateN]     = useState(false)
  const [editingNotebook, setEditingNotebook] = useState<NotebookSummary | null>(null)
  const [error, setError]                 = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [ps, ns] = await Promise.all([
        printablesApi.adminList(),
        notebooksApi.adminList(),
      ])
      setPrintables(ps)
      setNotebooks(ns)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Poll while anything is GENERATING
  useEffect(() => {
    const anyGenerating =
      printablesList.some(p => p.generationStatus === 'GENERATING') ||
      notebooksList.some(n => n.generationStatus === 'GENERATING')
    if (!anyGenerating) return
    const t = setTimeout(load, 3000)
    return () => clearTimeout(t)
  }, [printablesList, notebooksList, load])

  async function handleGeneratePrintable(id: number) {
    setGeneratingIds(prev => new Set(prev).add(id))
    try {
      await printablesApi.generate(id)
      setTimeout(load, 1000)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error')
    } finally {
      setGeneratingIds(prev => { const s = new Set(prev); s.delete(id); return s })
    }
  }

  async function handleTogglePublishPrintable(id: number, published: boolean) {
    try {
      const updated = await printablesApi.update(id, { published })
      setPrintables(prev => prev.map(p => p.id === id ? { ...p, published: updated.published } : p))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error')
    }
  }

  async function handleDeletePrintable(id: number) {
    try {
      await printablesApi.delete(id)
      setPrintables(prev => prev.filter(p => p.id !== id))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error')
    }
  }

  async function handleGenerateNotebook(id: number) {
    setGeneratingIds(prev => new Set(prev).add(id + 10000))
    try {
      await notebooksApi.generate(id)
      setTimeout(load, 1000)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error')
    } finally {
      setGeneratingIds(prev => { const s = new Set(prev); s.delete(id + 10000); return s })
    }
  }

  async function handleTogglePublishNotebook(id: number, published: boolean) {
    try {
      const updated = await notebooksApi.update(id, { published })
      setNotebooks(prev => prev.map(n => n.id === id ? { ...n, published: updated.published } : n))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error')
    }
  }

  async function handleDeleteNotebook(id: number) {
    try {
      await notebooksApi.delete(id)
      setNotebooks(prev => prev.filter(n => n.id !== id))
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error')
    }
  }

  function handleUpdateNotebook(updated: NotebookSummary) {
    setNotebooks(prev => prev.map(n => n.id === updated.id ? { ...n, ...updated } : n))
    setEditingNotebook(null)
  }

  return (
    <div style={pageStyles.page}>
      <PageHeader onBack={onBack} backLabel="← Administración" title="Imprimibles" />

      <div style={pageStyles.content}>
        {/* Tab bar */}
        <div style={pageStyles.tabBar}>
          <button
            style={{ ...pageStyles.tab, ...(tab === 'printables' ? pageStyles.tabActive : {}) }}
            onClick={() => setTab('printables')}
          >
            Imprimibles ({printablesList.length})
          </button>
          <button
            style={{ ...pageStyles.tab, ...(tab === 'notebooks' ? pageStyles.tabActive : {}) }}
            onClick={() => setTab('notebooks')}
          >
            Cuadernos ({notebooksList.length})
          </button>
        </div>

        {/* Action bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          {tab === 'printables' ? (
            <button style={pageStyles.createBtn} onClick={() => setShowCreateP(true)}>
              + Nuevo imprimible
            </button>
          ) : (
            <button style={pageStyles.createBtn} onClick={() => setShowCreateN(true)}>
              + Nuevo cuaderno
            </button>
          )}
        </div>

        {error && <p style={{ color: '#F44336', marginBottom: 12 }}>{error}</p>}

        {loading ? (
          <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Cargando…</p>
        ) : tab === 'printables' ? (
          printablesList.length === 0 ? (
            <p style={pageStyles.empty}>No hay imprimibles todavía. Crea el primero.</p>
          ) : (
            <div style={pageStyles.list}>
              {printablesList.map(p => (
                <PrintableRow
                  key={p.id}
                  p={p}
                  onGenerate={handleGeneratePrintable}
                  onTogglePublish={handleTogglePublishPrintable}
                  onDelete={handleDeletePrintable}
                  generating={generatingIds.has(p.id)}
                />
              ))}
            </div>
          )
        ) : (
          notebooksList.length === 0 ? (
            <p style={pageStyles.empty}>No hay cuadernos todavía.</p>
          ) : (
            <div style={pageStyles.list}>
              {notebooksList.map(n => (
                <NotebookRow
                  key={n.id}
                  n={n}
                  onGenerate={handleGenerateNotebook}
                  onTogglePublish={handleTogglePublishNotebook}
                  onDelete={handleDeleteNotebook}
                  onEdit={() => setEditingNotebook(n)}
                  generating={generatingIds.has(n.id + 10000)}
                />
              ))}
            </div>
          )
        )}
      </div>

      {showCreateP && (
        <CreatePrintableModal
          onClose={() => setShowCreateP(false)}
          onCreated={(pair) => { setPrintables(prev => [pair.es, pair.en, ...prev]); setShowCreateP(false) }}
        />
      )}

      {showCreateN && (
        <CreateNotebookModal
          onClose={() => setShowCreateN(false)}
          onCreated={(pair) => { setNotebooks(prev => [pair.es, pair.en, ...prev]); setShowCreateN(false) }}
        />
      )}

      {editingNotebook && (
        <EditNotebookModal
          notebook={editingNotebook}
          onClose={() => setEditingNotebook(null)}
          onUpdated={handleUpdateNotebook}
        />
      )}
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────────────────────

const pageStyles: Record<string, React.CSSProperties> = {
  page:    { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#FAF6EF' },
  content:   { flex: 1, padding: '32px 24px', maxWidth: 900, width: '100%', margin: '0 auto', boxSizing: 'border-box' },
  tabBar:    { display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #E8DDD0' },
  tab:       {
    padding: '10px 20px', border: 'none', background: 'transparent',
    cursor: 'pointer', fontSize: '0.95rem', color: '#888', borderRadius: '8px 8px 0 0',
  },
  tabActive: { background: '#fff', color: '#1B4332', fontWeight: 700, borderBottom: '2px solid #1B4332', marginBottom: -2 },
  createBtn: {
    background: '#1B4332', color: '#fff', border: 'none',
    borderRadius: 10, padding: '10px 20px', cursor: 'pointer',
    fontWeight: 700, fontSize: '0.9rem', minHeight: 44,
  },
  list:  { display: 'flex', flexDirection: 'column', gap: 8 },
  empty: { textAlign: 'center', color: '#888', padding: 40 },
}

const rowStyles: Record<string, React.CSSProperties> = {
  row: {
    display:       'flex',
    alignItems:    'flex-start',
    gap:           16,
    background:    '#fff',
    border:        '1px solid #E8DDD0',
    borderRadius:  12,
    padding:       '14px 16px',
    flexWrap:      'wrap',
  },
  info:    { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  title:   { fontWeight: 700, fontSize: '1rem', color: '#1A1A2E' },
  meta:    { fontSize: '0.875rem', color: '#888' },
  actions: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  btn: {
    border:         'none',
    borderRadius:   8,
    padding:        '6px 12px',
    cursor:         'pointer',
    fontSize: '0.875rem',
    fontWeight:     600,
    minHeight:      36,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
}

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position:       'fixed', inset: 0,
    background:     'rgba(0,0,0,0.5)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    zIndex:         1000,
    padding:        16,
  },
  box: {
    background:   '#fff',
    borderRadius: 16,
    padding:      '28px 24px',
    width:        '100%',
    maxWidth:     480,
    display:      'flex',
    flexDirection: 'column',
    gap:          8,
  },
  title:       { margin: '0 0 8px', fontFamily: "'Cormorant Garamond', serif", fontSize: '1.4rem', color: '#1B4332' },
  label:       { fontSize: '0.875rem', fontWeight: 700, color: '#555', marginTop: 4 },
  input: {
    width:        '100%',
    padding:      '8px 10px',
    borderRadius: 8,
    border:       '1.5px solid #DDD',
    fontSize:     '0.95rem',
    boxSizing:    'border-box',
    minHeight:    40,
  },
  error:       { color: '#C62828', fontSize: '0.875rem', margin: 0 },
  actions:     { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  btnPrimary:  { background: '#1B4332', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 700, minHeight: 44 },
  btnSecondary: { background: '#F0EAE0', color: '#333', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 700, minHeight: 44 },
}
